import React, { Component } from "react";
import { Route, Redirect, Switch, withRouter } from "react-router-dom";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { BackDrop } from "./components/BackDrop/BackDrop";
import { ProposalStepGuide } from "./components/ProposalStepGuide/proposalStepGuide";
import {
  getSubmissions,
  getAdditionalSubmissions,
  getProposalTypes,
  getCustomInfo,
  getRegistrationTypes,
  getRegistrationCategoryTypes,
  getLogos,
} from "./services/submissionService";
import { getVersions } from "./services/versionService";
import NavBar from "./components/navBar";
import LoginForm from "./components/loginForm";
import NotFound from "./components/notFound";
import Logout from "./components/logout";
import SideDrawer from "./components/SideDrawer/SideDrawer";
import ProtectedRoute from "./components/common/protectedRoute";
import ResetPassword from "./components/forgotPassword";
import User from "./components/user";
import LeftAlertDrawer from "./components/LeftAlerts/LeftAlertDrawer";
import Proposals from "./components/Proposals/proposals";
import ProposalSubmissions from "./components/ProposalSubmissions/proposalSubmissions";
import UpdatePropState from "./context/updatePropState";
import UpdateFormProposalType from "./context/updateFormProposalType";
import SessionLockModal from "./components/SessionLock/SessionLockModal";
import {
  clearSessionLockCookies,
  enterParticipatingContext,
  getActivityThrottleMs,
  getWarningState,
  isLocallyLocked,
  isSessionLockEnabled,
  lockSession,
  markActivity,
  markPresence,
  reconcileParticipatingContext,
  subscribeToLockEvents,
  verifyUnlock,
} from "./services/sessionLockService";
import {
  isEdgeSessionEnabled,
  sendHeartbeat,
} from "./services/sessionEdgeService";

import "./App.css";
import ProposalDocuments from "./components/Documents/proposalDocuments";
import FusionProposal from "./components/FusionProposal/fusionProposal";

class App extends Component {
  _isMounted = true;
  _edgeSessionEnabled = isEdgeSessionEnabled();

  state = {
    loggedIn: false,
    sideDrawerOpen: false,
    user: "",
    advisors: [],
    proposalTypes: [],
    proposals: [],
    additionalSubmissions: [], // Additional submissions data for pagination
    registrationTypes: [],
    registrationCategoryTypes: [],
    logos: [],
    versions: {},
    formProposalType: "",
    updateTime: null,
    editProposal: null,
    formRequestType: null,
    userInfo: [],
    details: false,
    submissionsRequestSent: false,
    userStatusRequestSent: false,
    submissionsLastKey: "",
    insertType: "",
    checkForUpdates: false,
    fusionStepGuideData: null, // Store data from step-guide for fusion workflow
    fusionListMode: false, // Flag to indicate when we're in fusion list mode
    lastUpdateDate: "",
    // Loading states for better UX
    proposalTypesLoading: false,
    userDataLoading: false, // Loading state for user data (enables card buttons)
    submissionsLoading: false, // Separate loading state for submissions data
    stepGuideDataLoading: false, // Separate loading state for step-guide required data
    dataLoaded: false, // Flag to prevent repeated data loading
    sessionLockEnabled: isSessionLockEnabled(),
    sessionLockWarningVisible: false,
    sessionLockRemainingSeconds: 0,
    sessionLocked: false,
    sessionLockCode: "",
    sessionLockError: "",
    sessionLockSubmitting: false,
  };

  handleLoggedIn = () => {
    this.setState({ loggedIn: true });
  };

  handleLogOut = () => {
    this.setState({ loggedIn: false });
  };

  parseApiResponseBody = (response, fallback = []) => {
    if (!response) return fallback;
    if (Array.isArray(response)) return response;

    const { body } = response;
    if (body === undefined || body === null || body === "") return fallback;
    if (typeof body === "string") return JSON.parse(body);
    return body;
  };

  parseLastEvaluatedKey = (response) => {
    if (!response || !response.LastEvaluatedKey) return "";

    const { LastEvaluatedKey } = response;
    if (typeof LastEvaluatedKey !== "string") return LastEvaluatedKey;
    if (LastEvaluatedKey === "" || LastEvaluatedKey === '""') return "";

    return JSON.parse(LastEvaluatedKey);
  };

  loadSubmissionsData = async () => {
    const firstPage = await getSubmissions().catch((e) => {
      console.log(`${e.message}: getSubmissions() : Please try again later`);
      throw e;
    });

    let proposals = this.parseApiResponseBody(firstPage, []);
    let submissionsLastKey = this.parseLastEvaluatedKey(firstPage);
    let additionalSubmissions = [];

    while (submissionsLastKey) {
      const nextPage = await getAdditionalSubmissions(
        1,
        JSON.stringify(submissionsLastKey),
      ).catch((e) => {
        console.log(
          `${e.message}: getAdditionalSubmissions() : Please try again later`,
        );
        throw e;
      });

      const nextSubmissions = this.parseApiResponseBody(nextPage, []);
      proposals = [...proposals, ...nextSubmissions];
      additionalSubmissions = [...additionalSubmissions, ...nextSubmissions];
      submissionsLastKey = this.parseLastEvaluatedKey(nextPage);
    }

    return { proposals, additionalSubmissions, submissionsLastKey };
  };

  getUserStatus = async () => {
    let currentUser = await getCurrentUser().catch((e) => {
      // User is not authenticated - this is expected
      return null;
    });

    if (currentUser) {
      // Get the session to access tokens
      let session = await fetchAuthSession().catch((e) => {
        console.log("Failed to fetch auth session:", e);
        return null;
      });

      if (session && session.tokens && session.tokens.accessToken) {
        this.handleLoggedIn();

        // Start version polling (every 5 minutes + on tab focus) — guard prevents duplicates
        if (!this.versionCheckInterval) {
          this._checkVersion = async () => {
            if (!this._isMounted) return;
            try {
              const res = await fetch(`/version.json?t=${Date.now()}`);
              if (!res.ok) return;
              const { version } = await res.json();
              if (version && version !== window.APP_VERSION) {
                console.info(
                  `New version detected (${window.APP_VERSION} → ${version}). Reloading.`
                );
                window.location.reload();
              }
            } catch (err) {
              console.warn("Version check failed:", err);
            }
          };
          this.versionCheckInterval = setInterval(this._checkVersion, 5 * 60 * 1000);
          this._onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
              this._checkVersion();
            }
          };
          document.addEventListener("visibilitychange", this._onVisibilityChange);
        }

      } else {
        // User exists in cookies but session/tokens are invalid (expired after inactivity)
        console.log(
          "User found but session is invalid/expired, redirecting to logout"
        );
        this.handleLogOut();
        window.location = "/logout";
        return;
      }

      this.setState({ user: currentUser.username });
      this.setGroups(
        session.tokens.accessToken.payload["cognito:groups"] || []
      );

      if (
        this._isMounted &&
        this.state.userStatusRequestSent === false &&
        this.state.loggedIn &&
        !this.state.dataLoaded
      ) {
        this.setState({
          userStatusRequestSent: true,
          proposalTypesLoading: true,
          userDataLoading: true,
          submissionsLoading: true,
          stepGuideDataLoading: true,
        });

        // PHASE 1: Load proposalTypes immediately (cards display)
        try {
          const proposalTypesResult = await getProposalTypes().catch((e) => {
            console.error("Failed to load proposal types:", e);
            throw e;
          });

          if (this._isMounted) {
            let parsedProposalTypes;

            // Handle the API response structure - it returns {statusCode: 200, body: "JSON_STRING"}
            try {
              let dataToProcess;
              if (proposalTypesResult && proposalTypesResult.body) {
                // Parse the JSON string from the body
                dataToProcess = JSON.parse(proposalTypesResult.body);
              } else if (Array.isArray(proposalTypesResult)) {
                dataToProcess = proposalTypesResult;
              } else {
                dataToProcess = proposalTypesResult;
              }

              if (Array.isArray(dataToProcess)) {
                parsedProposalTypes = dataToProcess;
              } else {
                console.warn(
                  "proposalTypes is not an array, using empty array"
                );
                parsedProposalTypes = [];
              }
            } catch (parseError) {
              console.error("Failed to parse proposal types:", parseError);
              parsedProposalTypes = [];
            }

            // Filter out disabled proposal types (enabled !== false)
            let enabledProposalTypes = [];
            if (Array.isArray(parsedProposalTypes)) {
              enabledProposalTypes = parsedProposalTypes.filter(
                (type) => type.enabled !== false
              );
            } else {
              // If parsedProposalTypes is not an array, treat it as empty
              enabledProposalTypes = [];
            }

            this.setState({
              proposalTypes: enabledProposalTypes,
              proposalTypesLoading: false,
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load proposal types:", e);
          if (this._isMounted) {
            this.setState({
              proposalTypesLoading: false,
              proposalTypes: [], // Set empty array as fallback
            });
          }
          // Only redirect to logout in production
          if (process.env.NODE_ENV === "production") {
            window.location = "/logout";
            return;
          }
        }

        // PHASE 2: Load user data (enables card buttons)
        try {
          const proposalsUserInfo = await getCustomInfo().catch((e) => {
            console.log(
              `${e.message}: getCustomInfo() : Please try again later`
            );
            throw e;
          });

          if (this._isMounted) {
            // Handle the API response structure for getCustomInfo
            let userInfoData;
            try {
              if (proposalsUserInfo && proposalsUserInfo.body) {
                // Parse the JSON string from the body
                userInfoData = JSON.parse(proposalsUserInfo.body);
              } else {
                userInfoData = proposalsUserInfo;
              }
            } catch (parseError) {
              console.error("Failed to parse user info:", parseError);
              userInfoData = {};
            }

            // Process advisors data
            const advisors = [];
            if (userInfoData) {
              Object.values(userInfoData.Advisors || {}).forEach((element) => {
                advisors.push(element);
              });
            }

            this.setState({
              userInfo: userInfoData,
              advisors: advisors,
              userDataLoading: false,
              // Keep stepGuideDataLoading true until submissions data is loaded
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load user data:", e);
          if (this._isMounted) {
            this.setState({
              userDataLoading: false,
              stepGuideDataLoading: false,
            });
          }
          // Only redirect to logout if it's an authentication error
          if (
            e.name === "NotAuthorizedException" ||
            e.name === "UnauthorizedException"
          ) {
            window.location = "/logout";
          }
          return;
        }

        // PHASE 3: Load remaining essential data in parallel
        const remainingDataPromises = [
          getRegistrationTypes().catch((e) => {
            console.log(
              `${e.message}: getRegistrationTypes() : Please try again later`
            );
            throw e;
          }),
          getRegistrationCategoryTypes().catch((e) => {
            console.log(
              `${e.message}: getRegistrationCategoryTypes() : Please try again later`
            );
            throw e;
          }),
          getLogos().catch((e) => {
            console.log(`${e.message}: getLogos() : Please try again later`);
            throw e;
          }),
          getVersions().catch((e) => {
            console.log(`${e.message}: getVersions() : Please try again later`);
            throw e;
          }),
        ];

        // Load remaining data needed for form functionality
        try {
          const [
            proposalRegTypesResult,
            proposalRegCategoryTypesResult,
            proposalLogos,
            currentVersions,
          ] = await Promise.all(remainingDataPromises);

          if (this._isMounted) {
            // Parse the response data for each API call
            let registrationTypes, registrationCategoryTypes, logos, versions;

            try {
              registrationTypes = proposalRegTypesResult?.body
                ? JSON.parse(proposalRegTypesResult.body)
                : proposalRegTypesResult;
            } catch (e) {
              console.error("Failed to parse registration types:", e);
              registrationTypes = [];
            }

            try {
              registrationCategoryTypes = proposalRegCategoryTypesResult?.body
                ? JSON.parse(proposalRegCategoryTypesResult.body)
                : proposalRegCategoryTypesResult;
            } catch (e) {
              console.error("Failed to parse registration category types:", e);
              registrationCategoryTypes = [];
            }

            try {
              logos = proposalLogos?.body
                ? JSON.parse(proposalLogos.body)
                : proposalLogos;
            } catch (e) {
              console.error("Failed to parse logos:", e);
              logos = {};
            }

            try {
              versions = currentVersions?.body
                ? JSON.parse(currentVersions.body)
                : currentVersions;
            } catch (e) {
              console.error("Failed to parse versions:", e);
              versions = {};
            }

            this.setState({
              registrationTypes: registrationTypes,
              registrationCategoryTypes: registrationCategoryTypes,
              logos: logos,
              versions: versions,
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load remaining data:", e);
          if (this._isMounted) {
            this.setState({
              // Continue even if some data failed
            });
          }
        }

        // PHASE 4: Load submissions data independently (required for step-guide)
        try {
          const submissionsData = await this.loadSubmissionsData();

          if (this._isMounted) {
            this.setState({
              proposals: submissionsData.proposals,
              additionalSubmissions: submissionsData.additionalSubmissions,
              submissionsLastKey: submissionsData.submissionsLastKey,
              submissionsLoading: false,
              stepGuideDataLoading: false, // Step-guide is now fully functional with all required data
              userStatusRequestSent: false,
              dataLoaded: true, // Mark data as loaded to prevent re-loading
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load submissions data:", e);
          if (this._isMounted) {
            this.setState({
              submissionsLoading: false,
              stepGuideDataLoading: false, // Allow step-guide to function even if submissions failed
              userStatusRequestSent: false,
              dataLoaded: true, // Still mark as loaded even on error
            });
          }
          // Only redirect to logout if it's an authentication error
          if (
            e.name === "NotAuthorizedException" ||
            e.name === "UnauthorizedException"
          ) {
            window.location = "/logout";
          }
        }
      }
    }
  };

  _logout = () => {
    this.clearSessionLockTimeouts();
    this.stopWarningCountdown();
    clearSessionLockCookies();
    window.location = "/logout";
  };

  handleSessionLockCodeChange = (sessionLockCode) => {
    this.setState({ sessionLockCode, sessionLockError: "" });
  };

  handleSessionUnlockConfirm = async () => {
    if (this.state.sessionLockSubmitting) {
      return;
    }

    this.setState({
      sessionLockSubmitting: true,
      sessionLockError: "",
    });

    try {
      let refreshedSession;
      try {
        refreshedSession = await fetchAuthSession({ forceRefresh: true });
      } catch (error) {
        console.error("Session refresh failed during unlock:", error);
        this._logout();
        return;
      }

      if (!refreshedSession?.tokens?.accessToken) {
        this._logout();
        return;
      }

      await verifyUnlock(this.state.sessionLockCode.trim());
      this.setState({
        sessionLocked: false,
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
        sessionLockCode: "",
        sessionLockError: "",
        sessionLockSubmitting: false,
      }, this.scheduleSessionLockTimers);
    } catch (error) {
      console.error("Failed to unlock session:", error);
      if (error?.message === "Session expired. Please log in again.") {
        this._logout();
        return;
      }
      this.setState({
        sessionLockSubmitting: false,
        sessionLockError:
          error?.message ||
          "Unable to confirm the code. Please try again or log out.",
      });
    }
  };

  clearSessionLockTimeouts = () => {
    if (this._sessionLockWarningTimer) {
      clearTimeout(this._sessionLockWarningTimer);
      this._sessionLockWarningTimer = null;
    }

    if (this._sessionLockTimer) {
      clearTimeout(this._sessionLockTimer);
      this._sessionLockTimer = null;
    }
  };

  stopWarningCountdown = (resetState = true) => {
    if (this._sessionLockCountdownInterval) {
      clearInterval(this._sessionLockCountdownInterval);
      this._sessionLockCountdownInterval = null;
    }

    if (
      resetState &&
      (this.state.sessionLockWarningVisible || this.state.sessionLockRemainingSeconds)
    ) {
      this.setState({
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
      });
    }
  };

  startWarningCountdown = (initialWarningState = getWarningState()) => {
    if (
      !this.state.sessionLockEnabled ||
      !this.state.loggedIn ||
      this.state.sessionLocked ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    if (initialWarningState.shouldLock) {
      this.triggerSessionLock();
      return;
    }

    if (!initialWarningState.shouldWarn) {
      this.stopWarningCountdown();
      return;
    }

    if (
      !this.state.sessionLockWarningVisible ||
      this.state.sessionLockRemainingSeconds !==
        initialWarningState.remainingSeconds
    ) {
      this.setState({
        sessionLockWarningVisible: true,
        sessionLockRemainingSeconds: initialWarningState.remainingSeconds,
      });
    }

    if (this._sessionLockCountdownInterval) {
      return;
    }

    this._sessionLockCountdownInterval = setInterval(() => {
      if (
        !this.state.sessionLockEnabled ||
        !this.state.loggedIn ||
        this.state.sessionLocked ||
        document.visibilityState !== "visible"
      ) {
        this.stopWarningCountdown();
        return;
      }

      const warningState = getWarningState();
      if (warningState.shouldLock || warningState.remainingSeconds <= 0) {
        this.stopWarningCountdown();
        this.triggerSessionLock();
        return;
      }

      if (!warningState.shouldWarn) {
        this.stopWarningCountdown();
        this.scheduleSessionLockTimers();
        return;
      }

      if (
        !this.state.sessionLockWarningVisible ||
        this.state.sessionLockRemainingSeconds !== warningState.remainingSeconds
      ) {
        this.setState({
          sessionLockWarningVisible: true,
          sessionLockRemainingSeconds: warningState.remainingSeconds,
        });
      }
    }, 1000);
  };

  scheduleSessionLockTimers = () => {
    this.clearSessionLockTimeouts();

    if (!this.state.sessionLockEnabled || !this.state.loggedIn) {
      this.stopWarningCountdown();
      return;
    }

    if (document.visibilityState !== "visible") {
      this.stopWarningCountdown();
      return;
    }

    if (isLocallyLocked()) {
      this.stopWarningCountdown();
      if (!this.state.sessionLocked) {
        this.setState({
          sessionLocked: true,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        });
      }
      return;
    }

    const warningState = getWarningState();
    if (warningState.shouldLock) {
      this.stopWarningCountdown();
      this.triggerSessionLock();
      return;
    }

    this._sessionLockTimer = setTimeout(() => {
      this.triggerSessionLock();
    }, warningState.msUntilLock);

    if (warningState.shouldWarn) {
      this.startWarningCountdown(warningState);
      return;
    }

    this.stopWarningCountdown();
    this._sessionLockWarningTimer = setTimeout(() => {
      this.startWarningCountdown();
    }, warningState.msUntilWarning);
  };

  initializeSessionLock = async () => {
    if (
      !this.state.sessionLockEnabled ||
      !this.state.loggedIn ||
      this._sessionLockInitialized
    ) {
      return;
    }

    this._sessionLockInitialized = true;
    const context = await enterParticipatingContext().catch((error) => {
      console.warn("Failed to initialize session lock context:", error);
      return null;
    });

    if (context?.startedFresh || context?.sessionChanged) {
      this.setState({
        sessionLocked: false,
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
        sessionLockCode: "",
        sessionLockError: "",
        sessionLockSubmitting: false,
      });
    }

    this._sessionLockBroadcastCleanup = subscribeToLockEvents((event) => {
      if (!event?.type) {
        return;
      }

      if (event.type === "lock") {
        this.clearSessionLockTimeouts();
        this.stopWarningCountdown();
        this.setState({
          sessionLocked: true,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        });
      }

      if (event.type === "unlock" || event.type === "reset") {
        this.setState({
          sessionLocked: false,
          sessionLockCode: "",
          sessionLockError: "",
          sessionLockSubmitting: false,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
        }, this.scheduleSessionLockTimers);
      }

      if (event.type === "activity") {
        const nextState = this.state.sessionLockWarningVisible
          ? {
              sessionLockWarningVisible: false,
              sessionLockRemainingSeconds: 0,
              sessionLockError: "",
            }
          : { sessionLockError: "" };
        this.setState(nextState, this.scheduleSessionLockTimers);
      }
    });

    this._handleSessionActivity = () => {
      if (
        document.visibilityState !== "visible" ||
        this.state.sessionLocked
      ) {
        return;
      }

      const now = Date.now();
      if (
        this._lastSessionActivityAt &&
        now - this._lastSessionActivityAt < getActivityThrottleMs()
      ) {
        return;
      }

      this._lastSessionActivityAt = now;
      markActivity();
      if (this.state.sessionLockWarningVisible || this.state.sessionLockError) {
        this.setState({
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        }, this.scheduleSessionLockTimers);
        return;
      }

      this.scheduleSessionLockTimers();
    };

    this._handleSessionVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        this.syncSessionLockState();
        return;
      }

      markPresence();
      this.clearSessionLockTimeouts();
      this.stopWarningCountdown();
    };

    this._handleSessionPageShow = () => {
      this.syncSessionLockState();
    };

    this._handleSessionPageHide = () => {
      markPresence();
      this.clearSessionLockTimeouts();
      this.stopWarningCountdown();
    };

    const activityEvents = [
      "pointerdown",
      "pointermove",
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      window.addEventListener(event, this._handleSessionActivity, {
        passive: true,
      });
    });
    document.addEventListener(
      "visibilitychange",
      this._handleSessionVisibilityChange
    );
    window.addEventListener("pageshow", this._handleSessionPageShow);
    window.addEventListener("pagehide", this._handleSessionPageHide);

    await this.syncSessionLockState();
  };

  teardownSessionLock = () => {
    this._sessionLockInitialized = false;
    this.clearSessionLockTimeouts();
    this.stopWarningCountdown();

    if (this._sessionLockBroadcastCleanup) {
      this._sessionLockBroadcastCleanup();
      this._sessionLockBroadcastCleanup = null;
    }

    if (this._handleSessionActivity) {
      const activityEvents = [
        "pointerdown",
        "pointermove",
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      activityEvents.forEach((event) => {
        window.removeEventListener(event, this._handleSessionActivity);
      });
      this._handleSessionActivity = null;
    }

    if (this._handleSessionVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this._handleSessionVisibilityChange
      );
      this._handleSessionVisibilityChange = null;
    }

    if (this._handleSessionPageShow) {
      window.removeEventListener("pageshow", this._handleSessionPageShow);
      this._handleSessionPageShow = null;
    }

    if (this._handleSessionPageHide) {
      window.removeEventListener("pagehide", this._handleSessionPageHide);
      this._handleSessionPageHide = null;
    }
  };

  syncSessionLockState = async () => {
    if (!this.state.sessionLockEnabled || !this.state.loggedIn) {
      return;
    }

    let context;
    try {
      context = await reconcileParticipatingContext();
    } catch (error) {
      console.warn("Failed to reconcile session lock context:", error);
      this._logout();
      return;
    }

    if (!this._isMounted || !this.state.loggedIn) {
      return;
    }

    if (context.locked) {
      this.clearSessionLockTimeouts();
      this.stopWarningCountdown();
      if (!this.state.sessionLocked) {
        this.setState({
          sessionLocked: true,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        });
      }
      return;
    }

    if (context.startedFresh || context.sessionChanged || this.state.sessionLocked) {
      this.setState({
        sessionLocked: false,
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
        sessionLockCode: "",
        sessionLockError: "",
        sessionLockSubmitting: false,
      }, this.scheduleSessionLockTimers);
      return;
    }

    this.scheduleSessionLockTimers();
  };

  triggerSessionLock = async () => {
    if (
      this._lockingSession ||
      !this.state.sessionLockEnabled ||
      !this.state.loggedIn ||
      this.state.sessionLocked ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    this._lockingSession = true;
    this.clearSessionLockTimeouts();
    this.stopWarningCountdown();

    try {
      await lockSession();
    } finally {
      this._lockingSession = false;
      this.setState({
        sessionLocked: true,
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
      });
    }
  };

  // ── Edge session lock methods (active when REACT_APP_VC_SESSION_EDGE_ENABLED=true) ──

  initializeEdgeSessionLock = async () => {
    if (
      !this._edgeSessionEnabled ||
      !this.state.loggedIn ||
      this._sessionLockInitialized
    ) {
      return;
    }

    this._sessionLockInitialized = true;

    // Initial heartbeat to ensure cookie exists
    await sendHeartbeat();

    // BroadcastChannel for heartbeat coordination (separate from legacy channel)
    if (typeof BroadcastChannel !== "undefined") {
      this._edgeHeartbeatChannel = new BroadcastChannel(
        "vc-session-edge-heartbeat"
      );
      this._edgeHeartbeatChannel.addEventListener("message", (event) => {
        if (event.data?.type === "heartbeat-sent") {
          this._lastEdgeHeartbeatAt = event.data.at || Date.now();
        }
      });
    }

    // Activity listener — throttled heartbeat on real user gestures
    this._lastEdgeHeartbeatAt = Date.now();

    this._handleEdgeActivity = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const now = Date.now();
      const throttle = 60000; // 1 heartbeat per 60s
      if (
        this._lastEdgeHeartbeatAt &&
        now - this._lastEdgeHeartbeatAt < throttle
      ) {
        return;
      }

      this._lastEdgeHeartbeatAt = now;
      sendHeartbeat();

      if (this._edgeHeartbeatChannel) {
        this._edgeHeartbeatChannel.postMessage({
          type: "heartbeat-sent",
          at: now,
        });
      }
    };

    const activityEvents = [
      "pointerdown",
      "pointermove",
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      window.addEventListener(event, this._handleEdgeActivity, {
        passive: true,
      });
    });
  };

  teardownEdgeSessionLock = () => {
    this._sessionLockInitialized = false;

    if (this._edgeHeartbeatChannel) {
      this._edgeHeartbeatChannel.close();
      this._edgeHeartbeatChannel = null;
    }

    if (this._handleEdgeActivity) {
      const activityEvents = [
        "pointerdown",
        "pointermove",
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      activityEvents.forEach((event) => {
        window.removeEventListener(event, this._handleEdgeActivity);
      });
      this._handleEdgeActivity = null;
    }
  };

  // ── End edge session lock methods ──

  setGroups = (inputGroups) => {
    this.setState({ groups: inputGroups });
  };

  componentDidMount = () => {
    this.getUserStatus();
    this.setupAuthErrorListener();
  };

  componentDidUpdate = (prevProps, prevState) => {
    const justLoggedIn = !prevState.loggedIn && this.state.loggedIn;
    const justLoggedOut = prevState.loggedIn && !this.state.loggedIn;

    if (justLoggedIn) {
      if (this._edgeSessionEnabled) {
        this.initializeEdgeSessionLock();
      } else {
        this.initializeSessionLock();
      }
    }

    if (justLoggedOut) {
      if (this._edgeSessionEnabled) {
        this.teardownEdgeSessionLock();
      } else {
        this.teardownSessionLock();
      }
    }
  };

  setupAuthErrorListener = () => {
    // Listen for auth errors from Amplify Hub
    Hub.listen("auth", ({ payload }) => {
      const { event } = payload;

      // Handle token refresh failures
      if (event === "tokenRefresh_failure") {
        console.log("Token refresh failed, redirecting to logout");
        window.location = "/logout";
      }

      // Handle sign out events
      if (event === "signedOut") {
        console.log("User signed out, redirecting to logout");
        window.location = "/logout";
      }
    });
  };

  componentWillUnmount = () => {
    this._isMounted = false;
    if (this._edgeSessionEnabled) {
      this.teardownEdgeSessionLock();
    } else {
      this.teardownSessionLock();
    }
    // Clear version polling
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
    if (this._onVisibilityChange) {
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
    }
  };

  drawerToggleClickHandler = () => {
    this.setState((prevState) => {
      return { sideDrawerOpen: !prevState.sideDrawerOpen };
    });
  };

  backdropClickHandler = () => {
    this.setState({ sideDrawerOpen: false });
  };

  sleep(milliseconds) {
    const date = Date.now();
    let currentDate = null;
    do {
      currentDate = Date.now();
    } while (currentDate - date < milliseconds);
  }

  updateProposalsState = async () => {
    if (
      this._isMounted &&
      this.state.submissionsRequestSent === false &&
      this.state.loggedIn
    ) {
      let currentUser = await getCurrentUser().catch((e) => {
        // User is not authenticated - this is expected
        return null;
      });
      if (currentUser) {
        this.setState({
          submissionsRequestSent: true,
          submissionsLoading: true,
        });

        try {
          // Only refresh submissions and proposal types for the refresh functionality
          const [proposalTypesResult, submissionsData] = await Promise.all([
            getProposalTypes().catch((e) => {
              console.error("Failed to load proposal types:", e);
              throw e;
            }),
            this.loadSubmissionsData(),
          ]);

          if (this._isMounted) {
            // Handle API response format
            let parsedProposalTypes;
            try {
              if (proposalTypesResult?.body) {
                parsedProposalTypes = [...JSON.parse(proposalTypesResult.body)];
              } else if (Array.isArray(proposalTypesResult)) {
                parsedProposalTypes = [...proposalTypesResult];
              } else {
                parsedProposalTypes = [];
              }
            } catch (e) {
              console.error("Error parsing proposal types:", e);
              parsedProposalTypes = [];
            }

            // Filter out disabled proposal types (enabled !== false)
            const newProposalTypes = parsedProposalTypes.filter(
              (type) => type.enabled !== false
            );

            this.setState({
              proposalTypes: newProposalTypes,
              proposals: submissionsData.proposals,
              additionalSubmissions: submissionsData.additionalSubmissions,
              submissionsLastKey: submissionsData.submissionsLastKey,
              updateTime: new Date().toLocaleString(),
              formProposalType: "",
              editProposal: null,
              formRequestType: null,
              submissionsRequestSent: false,
              submissionsLoading: false,
            });

            // No need to invalidate caches - removed caching logic
          }
        } catch (e) {
          console.error("Failed to update proposals state:", e);
          if (this._isMounted) {
            this.setState({
              submissionsRequestSent: false,
              submissionsLoading: false,
            });
          }
          // Don't redirect to logout for refresh failures
        }
      }
    }
  };

  updateFormProposalType = (type) => {
    this.setState({
      formProposalType: `${type}`,
      formRequestType: "NewSP",
      insertType: `${type}`.toLowerCase().includes("elements")
        ? "Snapshot"
        : "Fact Sheet",
    });
  };

  updateSubmission = (proposal) => {
    this.setState({ editProposal: proposal, formRequestType: "Edit" });
  };

  copySubmission = (proposal) => {
    // Extract account numbers from the proposal data
    let fusionAccountNumbers = [];

    // Check for direct "ACCOUNT NUMBERS" property (from submissions)
    if (proposal["ACCOUNT NUMBERS"]) {
      fusionAccountNumbers = proposal["ACCOUNT NUMBERS"]
        .split(",")
        .map((num) => num.trim())
        .filter((num) => num);
    }
    // Check for fusionAccountNumbers array
    else if (
      proposal.fusionAccountNumbers &&
      Array.isArray(proposal.fusionAccountNumbers)
    ) {
      fusionAccountNumbers = proposal.fusionAccountNumbers;
    }

    // Add account numbers to the proposal copy
    const proposalWithAccountNumbers = {
      ...proposal,
      fusionAccountNumbers: fusionAccountNumbers,
      isNewFusionClient: fusionAccountNumbers.length === 0,
    };

    this.setState({
      editProposal: proposalWithAccountNumbers,
      formRequestType: "Copy",
    });
  };

  newBlankProposal = () => {
    this.setState({ formRequestType: this.state.proposalTypes[0] });
  };

  // Handle fusion workflow data passing from step-guide to fusion form
  navigateToFusion = (stepGuideData) => {
    this.setState({
      fusionStepGuideData: stepGuideData,
      formProposalType: stepGuideData.program || "Fusion",
      fusionListMode: true, // Enable fusion list mode when entering fusion workflow
    });
    // Navigate to fusion using React Router
    this.props.history.push("/proposals/fusion");
  };

  // Enable fusion list mode (used when entering fusion workflow)
  enableFusionListMode = () => {
    this.setState({ fusionListMode: true });
  };

  // Disable fusion list mode (used when exiting fusion workflow)
  disableFusionListMode = () => {
    this.setState({ fusionListMode: false });
  };

  clearEditState = async () => {
    if (this._isMounted) {
      this.setState({ editProposal: null });
    }
  };

  updateDetails = () => {
    this.setState((prevState) => {
      return { ...prevState, showDetails: !prevState.showDetails };
    });
  };

  updateLastUpdateDate = (time) => {
    this.setState({ lastUpdateDate: time });
  };

  render() {
    let backdrop;
    const {
      user,
      proposals,
      additionalSubmissions,
      logos,
      proposalTypes,
      userInfo,
      advisors,
      registrationTypes,
      registrationCategoryTypes,
      showDetails,
      versions,
      insertType,
      checkForUpdates,
      lastUpdateDate,
      proposalTypesLoading,
      userDataLoading,
      submissionsLoading,
      stepGuideDataLoading,
    } = this.state;

    // Create simple lists object without caching complexity
    const lists = {
      proposals,
      additionalSubmissions,
      logos,
      proposalTypes,
      userInfo,
      advisors,
      registrationTypes,
      registrationCategoryTypes,
      versions,
    };

    if (this.state.sideDrawerOpen) {
      backdrop = <BackDrop click={this.backdropClickHandler} />;
    }

    return (
      <UpdatePropState.Provider value={this.updateProposalsState}>
        <UpdateFormProposalType.Provider vale={this.updateFormProposalType}>
          <div>
            <NavBar
              drawerClickHandler={this.drawerToggleClickHandler}
              user={user}
              proposalTypes={proposalTypes}
              proposals={proposals}
              newBlankProposal={this.newBlankProposal}
              proposalTypesLoading={proposalTypesLoading}
              submissionsLoading={submissionsLoading}
            />
            <LeftAlertDrawer
              show={
                this.state.sessionLockEnabled &&
                this.state.sessionLockWarningVisible &&
                !this.state.sessionLocked
              }
              message="Your session will lock soon due to inactivity."
              remainingSeconds={this.state.sessionLockRemainingSeconds}
              totalSeconds={120}
            />
            <SessionLockModal
              show={this.state.sessionLockEnabled && this.state.sessionLocked}
              code={this.state.sessionLockCode}
              error={this.state.sessionLockError}
              isSubmitting={this.state.sessionLockSubmitting}
              onCodeChange={this.handleSessionLockCodeChange}
              onConfirm={this.handleSessionUnlockConfirm}
              onLogout={this._logout}
            />
            <SideDrawer
              user={user}
              show={this.state.sideDrawerOpen}
              newBlankProposal={this.newBlankProposal}
            />
            {backdrop}
            <main style={{ paddingTop: "50px" }}>
              <Switch>
                <Route
                  path="/login"
                  component={(props) => (
                    <LoginForm {...props} userStatus={this.getUserStatus} />
                  )}
                />
                <Route
                  path="/logout"
                  component={(props) => (
                    <Logout
                      {...props}
                      userStatus={this.getUserStatus}
                      handleLogOut={this.handleLogOut}
                    />
                  )}
                />
                <Route path="/forgot" component={ResetPassword} />
                <ProtectedRoute
                  exact
                  path="/proposals"
                  render={(props) => (
                    <Proposals
                      {...props}
                      proposalTypes={this.state.proposalTypes}
                      updateFormProposalType={this.updateFormProposalType}
                      proposalTypesLoading={proposalTypesLoading}
                      userDataLoading={userDataLoading}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/proposals/step-guide"
                  component={(props) => (
                    <ProposalStepGuide
                      {...props}
                      updateLastUpdateDate={this.updateLastUpdateDate}
                      checkForUpdates={checkForUpdates}
                      lastUpdateDate={lastUpdateDate}
                      insertType={insertType}
                      lists={lists}
                      editProposal={this.state.editProposal}
                      formRequestType={this.state.formRequestType}
                      formProposalType={this.state.formProposalType}
                      loggedIn={this.state.loggedIn}
                      dataLoading={stepGuideDataLoading}
                      navigateToFusion={this.navigateToFusion}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/submissions"
                  component={(props) => (
                    <ProposalSubmissions
                      {...props}
                      showDetails={showDetails}
                      updateDetails={this.updateDetails}
                      newBlankProposal={this.newBlankProposal}
                      updateSubmission={this.updateSubmission}
                      copySubmission={this.copySubmission}
                      updateTime={this.state.updateTime}
                      proposalTypes={this.state.proposalTypes}
                      proposals={this.state.proposals}
                      updateProposalsState={this.updateProposalsState}
                      submissionsLastKey={this.state.submissionsLastKey}
                      submissionsRequestSent={this.state.submissionsRequestSent}
                      submissionsLoading={submissionsLoading}
                    />
                  )}
                />
                <ProtectedRoute path="/user" component={User} />
                <ProtectedRoute
                  path="/proposals/fusion"
                  component={(props) => (
                    <FusionProposal
                      {...props}
                      user={user}
                      proposalTypes={this.state.proposalTypes}
                      editProposal={this.state.editProposal}
                      formRequestType={this.state.formRequestType}
                      lists={lists}
                      fusionStepGuideData={this.state.fusionStepGuideData}
                      fusionListMode={this.state.fusionListMode}
                      enableFusionListMode={this.enableFusionListMode}
                      disableFusionListMode={this.disableFusionListMode}
                      redirectToStepGuideWithFusion={
                        this.redirectToStepGuideWithFusion
                      }
                    />
                  )}
                />
                <ProtectedRoute
                  path="/paperwork"
                  component={(props) => (
                    <ProposalDocuments
                      {...props}
                      user={user}
                      registrationTypes={registrationTypes}
                      userInfo={userInfo}
                      proposalTypes={this.state.proposalTypes}
                    />
                  )}
                />
                <Route path="/not-found" component={NotFound} />
                <Redirect from="/" exact to="/proposals" />
                <Redirect to="/not-found" />
              </Switch>
            </main>
          </div>
        </UpdateFormProposalType.Provider>
      </UpdatePropState.Provider>
    );
  }
}

export default withRouter(App);
